import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { action, computed, flow, observable, set } from "mobx";
import { ScannerResultExternalUrl } from "../models/scannerResultExternalUrl.js";
import { ScannerResultExternalUrlSingle } from "../models/scannerResultExternalUrlSingle.js";
import { ScannerResultTemplate } from "../models/scannerResultTemplate.js";
import { request } from "../utils/request.js";
import { locationRestScannerQueuePost } from "../wp-api/scannerQueue.post.js";
import { locationRestScannerResultAllExternalUrlsByHostGet } from "../wp-api/scannerResultsAllExternalUrls.get.js";
import { locationRestScannerResultExternalsGet } from "../wp-api/scannerResultsExternal.get.js";
import { locationRestScannerResultMarkupGet } from "../wp-api/scannerResultsMarkup.get.js";
import { locationRestScannerResultTemplatesGet } from "../wp-api/scannerResultsTemplate.get.js";
class ScannerStore {
    get sortedTemplates() {
        const result = Array.from(this.resultTemplates.values());
        // Move ignored items to the bottom list
        result.sort((a, b)=>{
            // MobX observability: `values()` does not check if `isIgnored` got updated, so we
            // need to trigger a read statement
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            a.data.consumerData.isIgnored;
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            b.data.consumerData.isIgnored;
            return a.data.headline.localeCompare(b.data.headline);
        });
        return result;
    }
    get sortedExternalUrls() {
        const result = Array.from(this.resultExternalUrls.values());
        // Move inactive items to the bottom list
        result.sort((a, b)=>a.inactive === b.inactive ? 0 : a.inactive ? 1 : -1);
        return result;
    }
    get templatesCount() {
        return this.fetchedAllResultTemplates ? this.resultTemplates.size : this.rootStore.optionStore.allScannerResultTemplatesCount;
    }
    get externalUrlsCount() {
        return this.fetchedAllResultExternalUrls ? this.resultExternalUrls.size : this.rootStore.optionStore.allScannerResultExternalUrlsCount;
    }
    get canShowResults() {
        return this.templatesCount + this.externalUrlsCount > 0 && this.rootStore.checklistStore.checklist?.items["scanner"].checked && this.rootStore.optionStore.others.isLicensed;
    }
    get foundScanResultsCount() {
        return this.resultTemplates.size + this.resultExternalUrls.size;
    }
    /**
     * Count of templates and external URL hosts which still needs attention.
     */ get needsAttentionCount() {
        return [
            ...this.resultTemplates.values(),
            ...this.resultExternalUrls.values()
        ].filter(({ inactive })=>!inactive).length;
    }
    constructor(rootStore){
        this.resultTemplates = new Map();
        this.busyResultTemplates = false;
        this.fetchedAllResultTemplates = false;
        this.resultExternalUrls = new Map();
        this.resultAllExternalUrls = new Map();
        this.busyExternalUrls = false;
        this.fetchedAllResultExternalUrls = false;
        this.busyMarkup = false;
        this.resultMarkup = new Map();
        this.addUrlsToQueue = flow(function*(data) {
            return yield request({
                location: locationRestScannerQueuePost,
                request: data
            });
        });
        this.fetchResultTemplates = flow(function*() {
            this.busyResultTemplates = true;
            try {
                this.resultTemplatesFromResponse((yield request({
                    location: locationRestScannerResultTemplatesGet
                })));
                this.fetchedAllResultTemplates = true;
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyResultTemplates = false;
            }
        });
        this.fetchResultExternals = flow(function*() {
            this.busyExternalUrls = true;
            try {
                this.resultExternalUrlsFromResponse((yield request({
                    location: locationRestScannerResultExternalsGet
                })));
                this.fetchedAllResultExternalUrls = true;
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyExternalUrls = false;
            }
        });
        this.fetchResultAllExternals = flow(function*(instance) {
            const type = instance instanceof ScannerResultExternalUrl ? "host" : "template";
            const { identifier } = instance;
            instance.busy = true;
            try {
                const { items } = yield request({
                    location: locationRestScannerResultAllExternalUrlsByHostGet,
                    params: {
                        type,
                        identifier: type === "host" ? identifier.replace(/\./g, "_") : identifier
                    }
                });
                // Check if map for this host already exists
                let map = this.resultAllExternalUrls.get(identifier);
                if (!map) {
                    map = new Map();
                } else {
                    // As we load all data within one request, we can safely remove stale objects
                    const existing = items.map(({ id })=>id);
                    for (const probablyStaleKey of map.keys()){
                        if (existing.indexOf(probablyStaleKey) === -1) {
                            map.delete(probablyStaleKey);
                        }
                    }
                }
                // Save templates as objects
                for (const item of Object.values(items)){
                    map.set(item.id, new ScannerResultExternalUrlSingle(item, this));
                    this.resultAllExternalUrls.set(identifier, map);
                }
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                instance.busy = false;
            }
        });
        this.fetchMarkup = flow(function*(id) {
            this.busyMarkup = true;
            try {
                const result = yield request({
                    location: locationRestScannerResultMarkupGet,
                    params: {
                        id
                    }
                });
                this.resultMarkup.set(id, result);
            } catch (e) {
                console.log(e);
                throw e;
            } finally{
                this.busyMarkup = false;
            }
        });
        this.rootStore = rootStore;
    }
    resultTemplatesFromResponse({ items }) {
        // As we load all data within one request, we can safely remove stale objects
        const existing = Object.keys(items);
        for (const probablyStaleKey of this.resultTemplates.keys()){
            if (existing.indexOf(probablyStaleKey) === -1) {
                this.resultTemplates.delete(probablyStaleKey);
            }
        }
        // Save template as object
        for (const pid of existing){
            this.resultTemplates.set(pid, new ScannerResultTemplate(items[pid], this));
        }
    }
    resultExternalUrlsFromResponse({ items }) {
        // As we load all data within one request, we can safely remove stale objects
        const existing = Object.keys(items);
        for (const probablyStaleKey of this.resultExternalUrls.keys()){
            if (existing.indexOf(probablyStaleKey) === -1) {
                this.resultExternalUrls.delete(probablyStaleKey);
            }
        }
        // Save templates as objects
        for (const externalHost of existing){
            const obj = this.resultExternalUrls.get(externalHost);
            if (obj) {
                set(obj, {
                    data: items[externalHost]
                });
            } else {
                this.resultExternalUrls.set(externalHost, new ScannerResultExternalUrl(items[externalHost], this));
            }
        }
    }
}
_ts_decorate([
    observable
], ScannerStore.prototype, "resultTemplates", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "busyResultTemplates", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "fetchedAllResultTemplates", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "resultExternalUrls", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "resultAllExternalUrls", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "busyExternalUrls", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "fetchedAllResultExternalUrls", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "busyMarkup", void 0);
_ts_decorate([
    observable
], ScannerStore.prototype, "resultMarkup", void 0);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "sortedTemplates", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "sortedExternalUrls", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "templatesCount", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "externalUrlsCount", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "canShowResults", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "foundScanResultsCount", null);
_ts_decorate([
    computed,
    _ts_metadata("design:type", void 0),
    _ts_metadata("design:paramtypes", [])
], ScannerStore.prototype, "needsAttentionCount", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof ResponseRouteScannerResultTemplatesGet === "undefined" ? Object : ResponseRouteScannerResultTemplatesGet
    ]),
    _ts_metadata("design:returntype", void 0)
], ScannerStore.prototype, "resultTemplatesFromResponse", null);
_ts_decorate([
    action,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        typeof ResponseRouteScannerResultExternalsGet === "undefined" ? Object : ResponseRouteScannerResultExternalsGet
    ]),
    _ts_metadata("design:returntype", void 0)
], ScannerStore.prototype, "resultExternalUrlsFromResponse", null);
export { ScannerStore };
